import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {WidgetData} from "../../../../../../../../core/shared/widget/widget-data";
import {WidgetDataParam} from "../../../../../../../../core/shared/widget/widget-data-param";
import {WidgetFactory} from "../../../../../../../../core/shared/widget/service/widget.factory";
import {QlQueryDto} from "../../../../../../../../core/shared/ql/data/dto/ql-query.dto";
import {QlWidgetFactory} from "../../../../../../../../core/shared/widget/service/ql-widget.factory";
import {FilterCriteria} from "../../../../../../../../core/shared/filter/api/filter.criteria";
import {Sorting} from "../../../../../../../../core/shared/misc/sorting";
import {FilterDate} from "../../../../../../../../core/shared/enum/mvs-filter-date.enum";
import {DatesHelper} from "../../../../../../../../core/shared/helper/dates-helper";
import {DtoList} from "../../../../../../../../core/shared/dto/dto.list";
import {
    FilterDateBetweenValues
} from "../../../../../../../../core/shared/components/mvs-dashboard-duration-filter/mvs-dashboard-duration-filter.component";
import {CreditorBankAccountService} from "../../../../../../service/api/creditor-bank-account.service";
import {CreditorBankAccountDto} from "../../../../../../model/dto/entity/creditor-bank-account.dto";

/**
 * Creditor-level transaction analysis chart.
 *
 * The generic chart is two-dimensional (booking day × payment direction), so this component adds
 * an ACCOUNT SELECTOR: the user picks one of the creditor's bank accounts and a TIME RANGE (default
 * = current ISO calendar week via FilterDate.CALENDAR_WEEK). The chart then renders that account's
 * incoming Forderungen (DEBIT) vs Gutschriften (CREDIT) per day for the selected range, with a
 * metric toggle between the transaction count ("Anzahl") and the summed amount ("Summe"). Data is a
 * QL groupBy over bm.CreditorBankAccountTransaction filtered by the selected account + booking-date
 * range, grouped by (bookingDate, paymentDirection) with count and sum(amount).
 */
@Component({
    selector: 'bm-creditor-transaction-analysis',
    templateUrl: 'bm-creditor-transaction-analysis.component.html',
    standalone: false
})
export class BmCreditorTransactionAnalysisComponent implements OnInit, OnChanges, OnDestroy {

    initialized: boolean;
    accountsLoading: boolean = false;

    @Input() creditorId: number;

    accounts: CreditorBankAccountDto[] = [];
    selectedAccountId: number;

    // Booking-date range currently applied (resolved from the range selector; default = calendar week).
    rangeStart: string;
    rangeEnd: string;

    // Metric toggle: 'count' shows the number of transactions, 'sum' the summed amount.
    selectedMetric: 'count' | 'sum' = 'count';
    metricOptions = [
        {label: 'Anzahl', value: 'count'},
        {label: 'Summe', value: 'sum'}
    ];

    chartWidget: WidgetData;

    // Exposed for the template's [defaultFilterDate] binding.
    protected readonly FilterDate = FilterDate;

    constructor(protected creditorBankAccountService: CreditorBankAccountService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        if (!this.creditorId) {
            return;
        }

        // Default booking-date range = current ISO calendar week (Mon–Sun). The range selector lives
        // inside a lazily-rendered popover, so it cannot emit its default before the user opens it;
        // we therefore seed the same CALENDAR_WEEK range here so the chart shows the current week
        // immediately. User changes then flow in via handleRangeValues().
        const today = new Date();
        this.rangeStart = this.toLocalDateString(DatesHelper.startOfIsoWeek(today));
        this.rangeEnd = this.toLocalDateString(DatesHelper.endOfIsoWeek(today));

        this.loadAccounts();
    }

    /**
     * Format a Date as a local date-only string (yyyy-MM-dd), matching the LocalDate bookingDate
     * field without any UTC shift.
     */
    private toLocalDateString(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    /**
     * Load the creditor's bank accounts to populate the account selector, defaulting the selection
     * to the first account. The range selector emits its default (calendar-week) range once the
     * template renders, which triggers the initial chart build.
     */
    loadAccounts(): void {

        this.accountsLoading = true;

        this.creditorBankAccountService.listByAttribute('creditor', this.creditorId, [new Sorting('name', true)]).subscribe(
            (list: DtoList) => {
                this.accounts = (list?.entries || []) as CreditorBankAccountDto[];
                if (this.accounts.length && !this.selectedAccountId) {
                    this.selectedAccountId = this.accounts[0].id;
                }
                this.accountsLoading = false;
                this.initialized = true;
                this.buildChartWidget();
            },
            () => {
                this.accounts = [];
                this.accountsLoading = false;
                this.initialized = true;
            }
        );
    }

    /**
     * Build the chart widget once an account and a booking-date range are both available.
     */
    buildChartWidget(): void {

        if (!this.selectedAccountId || !this.rangeStart || !this.rangeEnd) {
            this.chartWidget = null;
            return;
        }

        const accountFilter = FilterCriteria.create(
            'e.creditorBankAccountId',
            FilterCriteria.cOperatorEqual,
            this.selectedAccountId
        );

        const dateFilter = FilterCriteria.create(
            'e.bookingDate',
            FilterCriteria.cOperatorBetween,
            this.rangeStart,
            this.rangeEnd
        );

        // QL groupBy over the selected account's transactions within the range: one row per
        // (bookingDate, paymentDirection) carrying both the count and the summed amount.
        const qlQuery: QlQueryDto = {
            name: 'bm.creditor.transactionAnalysis.chart',
            type: 'view',
            start: {
                type: 'groupBy',
                name: 'bm.CreditorBankAccountTransaction',
                as: 'e',
                fields: [
                    {name: 'bookingDate', fieldType: 'regular', dataType: 'LocalDate'},
                    {name: 'paymentDirection', fieldType: 'regular'},
                    {name: '*', function: 'count', as: 'transactionCount', fieldType: 'function', dataType: 'Long'},
                    {name: 'amount', function: 'sum', as: 'amountSum', fieldType: 'function', dataType: 'BigDecimal'}
                ]
            },
            fixedFilters: [accountFilter, dateFilter],
            sortings: [new Sorting('e.bookingDate', true)]
        };

        const aggregateField = this.selectedMetric === 'count' ? 'e.transactionCount' : 'e.amountSum';
        const title = this.selectedMetric === 'count'
            ? 'Transaktionen je Tag (Anzahl)'
            : 'Transaktionen je Tag (Summe)';

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Grouped bar chart of a creditor bank account's transactions per booking day for the
         *         selected time range, split into Forderungen/Gutschriften, aggregated by count or sum
         */
        this.chartWidget = WidgetFactory.createWidgetList(
            'bm.creditor.transactionAnalysis.chart',
            title,
            'chart',
            'list',
            'ql',
            '',
            'Keine Transaktionen im gewählten Zeitraum',
            QlWidgetFactory.createWidgetRequest(qlQuery, 'resolved'),
            WidgetDataParam.create('chartType', 'bar'),
            WidgetDataParam.create('chartField1st', 'e.bookingDate'),
            WidgetDataParam.create('chartField2nd', 'e.paymentDirectionEnumName'),
            WidgetDataParam.create('chartFieldAggregate', aggregateField),
            WidgetDataParam.create('chartAggregateFunction', 'sum'),
            WidgetDataParam.create('chartStacked', false),
            WidgetDataParam.create('size', 'M')
        );
    }

    /**
     * Handle the applied booking-date range from the range selector overlay (localDate mode gives
     * date-only strings, matching the LocalDate bookingDate field).
     */
    handleRangeValues(values: FilterDateBetweenValues): void {
        this.rangeStart = values?.startDate;
        this.rangeEnd = values?.endDate;
        this.buildChartWidget();
    }

    /**
     * Handle account selection change: rebuild the chart for the newly selected account.
     */
    handleAccountChange(): void {
        this.buildChartWidget();
    }

    /**
     * Handle metric toggle change (Anzahl / Summe): rebuild the chart with the matching aggregate.
     */
    handleMetricChange(): void {
        this.buildChartWidget();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes["creditorId"]) {
            this.selectedAccountId = null;
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }
}
